import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Button,
  Divider,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { NotificationManager } from "react-notifications";
import {
  DeleteSupplyOrderAttachment,
  GetSupplyOrderAttachments,
  UploadSupplyOrderAttachment,
} from "../../../services/InventoryService";
import CommonLoader from "../../common/CommonLoader";
import DeleteConfirmation from "../../common/DeleteConfirmation";
import RowActionsMenu from "../../common/RowActionsMenu";

// Keep in step with SupplyOrderAttachmentService on the API.
const MAX_FILE_SIZE_MB = 50;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const DEFAULT_CATEGORY = "Supply Order Docs";
const categoryOptions = [
  DEFAULT_CATEGORY,
  "Supply Order Quote",
  "Supply Order Invoice",
  "Supply Order Packing Slip",
  "Supply Order Receipt",
];
const acceptedFileTypes =
  ".pdf,.png,.jpg,.jpeg,.gif,.bmp,.tif,.tiff,.doc,.docx,.xls,.xlsx,.csv,.txt,.msg,.eml";

// Supply order version of OrderAttachmentsTable (lab orders). Files go to the share and
// webdocs keeps the metadata row; readOnly hides upload/delete for the view screen.
const SupplyOrderAttachmentsTable = ({ poNumber, readOnly = false }) => {
  const columns = ["Title", "Category", "Uploaded", "Actions"];
  const rowsPerPageOptions = [5, 10, 25];

  const [attachments, setAttachments] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [loading, setLoading] = useState(true);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
  const [selectedAttachmentToDelete, setSelectedAttachmentToDelete] = useState(null);

  // Upload dialog states
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [uploadTitle, setUploadTitle] = useState("");
  const [uploadCategory, setUploadCategory] = useState(DEFAULT_CATEGORY);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (poNumber) {
      fetchAttachments();
    }
  }, [poNumber]);

  const fetchAttachments = async () => {
    try {
      setLoading(true);
      const data = await GetSupplyOrderAttachments(poNumber);
      setAttachments(data.results || data || []);
    } catch (error) {
      console.error("Error fetching attachments:", error);
      NotificationManager.error("Error fetching attachments");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const resetUploadDialog = () => {
    setUploadTitle("");
    setUploadCategory(DEFAULT_CATEGORY);
    setSelectedFile(null);
  };

  const handleAddClick = () => {
    resetUploadDialog();
    setIsUploadDialogOpen(true);
  };

  const handleUploadDialogClose = () => {
    setIsUploadDialogOpen(false);
    resetUploadDialog();
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    // Clear the input so re-picking the same file after a rejection still fires onChange.
    event.target.value = "";
    if (!file) return;

    if (file.size > MAX_FILE_SIZE_BYTES) {
      NotificationManager.warning(`File must be ${MAX_FILE_SIZE_MB} MB or smaller`);
      return;
    }

    setSelectedFile(file);
    // Auto-populate title with filename if title is empty
    if (!uploadTitle) {
      setUploadTitle(file.name);
    }
  };

  const handleUploadSubmit = async () => {
    if (!selectedFile) {
      NotificationManager.warning("Please select a file to upload");
      return;
    }

    if (!uploadTitle.trim()) {
      NotificationManager.warning("Please enter a title for the attachment");
      return;
    }

    try {
      setUploading(true);

      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("title", uploadTitle.trim());
      formData.append("category", uploadCategory);

      await UploadSupplyOrderAttachment(poNumber, formData);

      NotificationManager.success("Attachment uploaded successfully");
      handleUploadDialogClose();
      await fetchAttachments();
    } catch (error) {
      console.error("Error uploading attachment:", error);
      // The API returns the validation reason (type, size, name) as the body on a 400.
      let message = "Error uploading attachment. Please try again.";
      try {
        const body = await error.response?.text();
        if (body && error.response?.status === 400) message = body;
      } catch (_) {
        // keep the generic message
      }
      NotificationManager.error(message);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteClick = (attachment) => {
    setSelectedAttachmentToDelete(attachment);
    setIsDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirmationClose = () => {
    setIsDeleteConfirmationOpen(false);
  };

  const handleDeleteConfirmationConfirm = async () => {
    try {
      await DeleteSupplyOrderAttachment(poNumber, selectedAttachmentToDelete.recordid);
      NotificationManager.success("Attachment deleted successfully");
      setAttachments(
        attachments.filter((a) => a.recordid !== selectedAttachmentToDelete.recordid)
      );
    } catch (error) {
      console.error("Error deleting attachment:", error);
      NotificationManager.error("Error deleting attachment");
    } finally {
      setIsDeleteConfirmationOpen(false);
    }
  };

  const handleViewClick = (attachment) => {
    const viewerUrl = `/vlp/inventory-supply-orders/${encodeURIComponent(
      poNumber
    )}/document/${encodeURIComponent(attachment.physicalfile)}`;

    window.open(viewerUrl, "_blank");
  };

  const formatUploadTime = (value) =>
    value ? new Date(value).toLocaleDateString() : "";

  const renderActions = (attachment) => {
    const actions = [
      {
        label: "View",
        icon: <VisibilityIcon />,
        color: "green",
        onClick: () => handleViewClick(attachment),
      },
    ];

    if (!readOnly) {
      actions.push({
        label: "Delete",
        icon: <DeleteIcon />,
        color: "red",
        onClick: () => handleDeleteClick(attachment),
      });
    }

    return (
      <RowActionsMenu
        ariaLabel={`actions for attachment ${attachment.title || ""}`}
        actions={actions}
      />
    );
  };

  return (
    <Paper elevation={3} sx={{ padding: 3 }} style={{ maxWidth: "750px", marginTop: "16px" }}>
      <Typography variant="h13" style={{ color: "rgb(25,118,210)" }}>
        Order Attachments
      </Typography>

      <Divider sx={{ mb: 1 }} />
      {!readOnly && (
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAddClick}
          sx={{ mb: 2 }}
        >
          New
        </Button>
      )}
      <TableContainer>
        <Table sx={{ minWidth: 550 }} size="small">
          <TableHead
            sx={{
              backgroundColor: (theme) => theme.palette.primary.main,
              color: "white",
            }}
          >
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column}>{column}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {attachments
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((attachment, index) => (
                <TableRow
                  key={attachment.recordid}
                  sx={{
                    backgroundColor:
                      index % 2 === 0 ? "rgba(0, 0, 0, 0.04)" : "white",
                  }}
                >
                  <TableCell>{attachment.title}</TableCell>
                  <TableCell>{attachment.webdocsCategory}</TableCell>
                  <TableCell>{formatUploadTime(attachment.lastUploadTime)}</TableCell>
                  <TableCell>{renderActions(attachment)}</TableCell>
                </TableRow>
              ))}
            {!loading && attachments.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length}>
                  <Typography variant="body2" color="text.secondary">
                    No attachments yet.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={rowsPerPageOptions}
        component="div"
        count={attachments.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      {/* Upload Dialog */}
      <Dialog
        open={isUploadDialogOpen}
        onClose={handleUploadDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Upload Attachment</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
            <TextField
              label="Title"
              value={uploadTitle}
              onChange={(e) => setUploadTitle(e.target.value)}
              fullWidth
              required
              placeholder="Enter attachment title"
            />

            <FormControl fullWidth required>
              <InputLabel>Category</InputLabel>
              <Select
                value={uploadCategory}
                label="Category"
                onChange={(e) => setUploadCategory(e.target.value)}
              >
                {categoryOptions.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box>
              <input
                accept={acceptedFileTypes}
                style={{ display: "none" }}
                id="supply-order-file-upload-input"
                type="file"
                onChange={handleFileSelect}
              />
              <label htmlFor="supply-order-file-upload-input">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUploadIcon />}
                  fullWidth
                >
                  Choose File
                </Button>
              </label>
              <Typography variant="caption" sx={{ display: "block", mt: 1, color: "text.secondary" }}>
                Max {MAX_FILE_SIZE_MB} MB
              </Typography>
              {selectedFile && (
                <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
                  Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUploadDialogClose} disabled={uploading}>
            Cancel
          </Button>
          <Button
            onClick={handleUploadSubmit}
            variant="contained"
            color="primary"
            disabled={uploading || !selectedFile}
            startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
          >
            {uploading ? "Uploading..." : "Upload"}
          </Button>
        </DialogActions>
      </Dialog>

      <DeleteConfirmation
        isOpen={isDeleteConfirmationOpen}
        onClose={handleDeleteConfirmationClose}
        onConfirm={handleDeleteConfirmationConfirm}
        title="Delete Confirmation"
        message="Are you sure you want to delete this attachment?"
        color="red"
      />
      {loading && <CommonLoader />}
    </Paper>
  );
};

export default SupplyOrderAttachmentsTable;
